import cx from 'classnames';
import { type FunctionComponent, type ReactChild, type ReactNode } from 'react';

import { Action } from '../../../../typings/permissions';
import { userHasPermissionForPolicyType } from '../../../helpers/permissions';
import Badge from '../../generic/Badge/Badge';
import Button from '../../generic/Button';
import Flex from '../../generic/Flex';
import Icon from '../../generic/Icon';
import List from '../../generic/List';
import type { LicensingRuleType } from '../../../../shared/model-typings';
import type { LicenseResponse } from '../../../hooks/api/licenses/useLicense';
import type { LicensingRule, Policy } from '../../../types';
import type { IconClassName } from '../../generic/Icon';

import { ActionDropdown } from './ActionDropdown';
import { RuleItem } from './RuleItem';

import styles from './Rules.module.less';

type RuleListProps = {
  policy: Policy;
  title: string;
  icon: IconClassName;
  search?: string;
  ruleType?: LicensingRuleType;
  isDefault?: boolean;
  uncategorizedLicenses?: LicenseResponse[];
  selectedLicenses: Set<string>;
  isDeleteEnabled?: boolean;
  setSelectedLicenses: (selectedLicenses: Set<string>) => void;
  onRuleAdd?(defaultRule?: LicensingRule): void;
  onSetAsDefault?(): void;
  onCheck: (isChecked: boolean, licenseId: string, ruleType?: LicensingRuleType) => void;
  onBulkAction?(): void;
  onBulkDelete: (selectedLicenses: Set<string>) => void;
  isDeleting: boolean;
  onLicenseClick: (licenseId: string | null, ruleId: number | null) => void;
  searchLicense: (license: LicenseResponse) => boolean;
  licenseNamesMap?: Record<string, string>;
};

const RuleList: FunctionComponent<RuleListProps> = (props) => {
  const rulesInList = props.policy
    ? (props.policy.rules as LicensingRule[])
        .filter((r) => r.type === props.ruleType)
        .filter(
          (r) =>
            !props.search ||
            (r.license && props.searchLicense(r.license)) ||
            (props.licenseNamesMap?.[r.licenseId] &&
              props.searchLicense({
                licenseId: r.licenseId,
                name: props.licenseNamesMap[r.licenseId]!,
              } as LicenseResponse)),
        )
        .sort((a, b) => {
          // Try to sort by the display name
          if (props.licenseNamesMap?.[a.licenseId] && props.licenseNamesMap?.[b.licenseId]) {
            return props.licenseNamesMap[a.licenseId]!.toLowerCase().localeCompare(
              props.licenseNamesMap[b.licenseId]!.toLowerCase(),
            );
          }
          // Fallback to the license ID
          return (props.licenseNamesMap?.[a.licenseId] ?? a.licenseId)
            .toLowerCase()
            .localeCompare((props.licenseNamesMap?.[b.licenseId] ?? b.licenseId).toLowerCase());
        })
    : [];

  const renderedList =
    rulesInList.length > 0
      ? rulesInList.map((rule) => {
        // `ruleList.length > 0` implies `!!props.policy` since `Array#filter` and `Array#map` are synchronous.
        return (
          <RuleItem
            key={`$rule-${rule.ruleId}`}
            ruleType={props.ruleType}
            rule={rule}
            isChecked={props.selectedLicenses.has(rule.licenseId)}
            onCheck={props.onCheck}
            onLicenseClick={props.onLicenseClick}
            licenseName={props.licenseNamesMap?.[rule.licenseId]}
          />
        ) as ReactNode;
      })
      : [];

  const canEdit = userHasPermissionForPolicyType(Action.Edit, props.policy.type);

  const selectAllLicenses = () => {
    if (props.ruleType) {
      props.setSelectedLicenses(new Set(rulesInList.map((r) => r.licenseId)));
    } else if (props.uncategorizedLicenses) {
      props.setSelectedLicenses(new Set(props.uncategorizedLicenses.map((l) => l.licenseId)));
    } else {
      props.setSelectedLicenses(new Set());
    }
  };

  const listContent: ReactNode | ReactNode[] =
    props.children ||
    (renderedList.length > 0 ? renderedList : <div className={styles.emptyMessage}>No rules configured.</div>);

  return (
    <div className={styles.ruleList}>
      <List>
        <List.Header>
          <Flex justify="space-between" gap={10}>
            <h4>
              <Flex gap={5}>
                <Flex gap={5}>
                  <div
                    className={cx(styles.policyColHeader, {
                      [styles.denied]: props.ruleType === 'denied_license',
                      [styles.flagged]: props.ruleType === 'flagged_license',
                      [styles.approved]: props.ruleType === 'approved_license',
                    })}
                  >
                    <Icon icon={props.icon} />
                  </div>
                  <span className={styles.headerText}>{props.title}</span>
                </Flex>
                <Badge color="light-gray" size="micro" shape="circle">
                  {props.selectedLicenses.size > 0 ? `${props.selectedLicenses.size} / ` : ''}
                  {props.children ? (props.children as ReactChild[]).length : renderedList.length}
                </Badge>
              </Flex>
            </h4>
            <Flex gap={12}>
              {props.isDefault && (
                <span>
                  <Badge color="dark-gray" size="micro">
                    Default
                  </Badge>
                </span>
              )}
              {canEdit && props.selectedLicenses.size > 0 && (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => props.onBulkAction?.()}
                >
                  {props.ruleType ? 'Edit selected' : 'Move selected'}
                </Button>
              )}
              {canEdit && (
                <ActionDropdown
                  ruleType={props.ruleType}
                  selectedLicenses={props.selectedLicenses}
                  uncategorizedLicenses={props.uncategorizedLicenses}
                  rulesInList={rulesInList}
                  isDefault={!!props.isDefault}
                  isDeleteEnabled={props.isDeleteEnabled}
                  onRuleAdd={props.onRuleAdd}
                  onSetAsDefault={props.onSetAsDefault}
                  onBulkAction={props.onBulkAction}
                  onBulkDelete={() => props.onBulkDelete(props.selectedLicenses)}
                  selectAllLicenses={selectAllLicenses}
                  setSelectedLicenses={props.setSelectedLicenses}
                />
              )}
            </Flex>
          </Flex>
        </List.Header>
        <div className={styles.list}>
          <List.Body>{!props.isDeleting && listContent}</List.Body>
        </div>
      </List>
    </div>
  );
};

export default RuleList;
